<script setup lang="ts">
import { computed } from 'vue'

import type { DayData, MessageAction } from './types'

interface Props {
  days: DayData[]
  arePricesDisplayed: boolean
}

const props = defineProps<Props>()

// Get unique annotations from all days
const messageActions = computed(() => {
  const seen = new Set<string>()
  const result: MessageAction[] = []

  for (const day of props.days) {
    for (const annotation of day.annotations) {
      const key = `${annotation.style}:${annotation.message}`
      if (!seen.has(key)) {
        seen.add(key)
        result.push(annotation)
      }
    }
  }

  return result
})

const areAnySoldOut = computed(() => {
  return props.days.some((day) => day.status === 'sold_out')
})

// TODO: Support advertise_price_including_fees. When prices already include fees, this legend
// should not show "Prices exclude taxes and fees" (or should show different copy). Currently
// the message is always "exclude" when prices are shown, which is wrong for include-fees configs.
const taxesAndFeesMessage = computed(() => {
  if (!props.arePricesDisplayed) return ''
  return 'Prices exclude taxes and fees'
})

const hasItems = computed(() => {
  return messageActions.value.length > 0 || areAnySoldOut.value || taxesAndFeesMessage.value
})
</script>

<template>
  <div v-if="hasItems" aria-hidden="true" class="calendar-legend">
    <div v-if="areAnySoldOut" class="legend-item">
      <span class="sold-out-indicator"></span>
      <span>Sold out</span>
    </div>

    <div v-for="(item, index) in messageActions" :key="index" class="legend-item">
      <span class="dot" :class="item.style"></span>
      <span>{{ item.message }}</span>
    </div>

    <!-- TODO: Currency symbol hardcoded ($). Wrong for non-USD tenants. Not yet clear how currency should be passed in. -->
    <div v-if="taxesAndFeesMessage" class="legend-item">
      <span class="taxes-fees">$</span>
      <span>{{ taxesAndFeesMessage }}</span>
    </div>
  </div>
</template>

<style scoped>
.calendar-legend {
  border-top: 1px solid var(--neutral-border-color);
  padding-top: 16px;
  margin-top: 16px;
  margin-bottom: 0;
  display: flex;
  flex-wrap: wrap;
  gap: 16px;

  @media (--mobile-experience) {
    padding: 8px 16px 10px;
    margin-top: 0;
    box-shadow: 0 -4px 8px rgba(0, 0, 0, 0.08);
    background: var(--elevation-0);
  }

  .legend-item {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    color: var(--text-secondary);

    .sold-out-indicator {
      width: 16px;
      height: 2px;
      background: var(--text-tertiary);
    }

    .dot {
      width: 8px;
      height: 8px;
      border-radius: 50%;

      &.info {
        background: var(--info-text-color);
      }

      &.warning {
        background: var(--warning-text-color);
      }

      &.error {
        background: var(--error-text-color);
      }

      &.success {
        background: var(--success-text-color);
      }
    }

    .taxes-fees {
      font-weight: 600;
      font-size: 10px;
      color: var(--text-secondary);
    }
  }
}
</style>
